"use client";

import { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { Avatar, Card, Button, Tag } from '@/components/ds';
import { Textarea } from '@/components/ui/textarea';
import { insertEmojiAtCursor } from '@/lib/insertEmojiAtCursor';
import { useCaretRestore } from '@/hooks/useCaretRestore';

// Deferred so `emoji-picker-react` stays out of the feed's initial bundle.
// The popover statically imports the library's Theme/EmojiStyle enums (runtime
// values, not types), so a static import here would pull the whole picker into
// every feed page even though the picker component itself is lazy.
const EmojiPickerPopover = dynamic(() => import('@/components/dm/EmojiPickerPopover'), {
  ssr: false,
});
import {
  MessageCircle,
  Mic,
  Play,
  Music,
  Video,
  Headphones,
  Image as ImageIcon,
  Smile,
  Sparkles,
  TrendingUp,
  Clock,
  Activity,
  GraduationCap,
  Lightbulb,
  PartyPopper,
  Tag as TagIcon,
  ArrowRight,
  X,
} from 'lucide-react';
import { useAuthContext } from './providers/AuthProvider';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useCommunityPosts } from '@/hooks/useCommunityPosts';
import { useCommunityStatistics } from '@/hooks/useCommunityStatistics';
import PostCategoryNavigation from './PostCategoryNavigation';
import PostItem from './PostItem';
import { EmptyState } from './EmptyState';
import TagAutocomplete from './TagAutocomplete';
import RecentJobsWidget from './home/RecentJobsWidget';
import { cn } from '@/lib/utils';
import { createClient } from '@/lib/supabase/browser';
import { resumableUpload, UploadProgress } from '@/lib/resumableUpload';
import { resolveDisplayName } from '@/lib/displayName';

type FeedTab = 'foryou' | 'trending' | 'latest';
type PostCategory = 'general' | 'workshop' | 'audition' | 'demo' | 'tip' | 'celebration';

export default function InteractiveHomeFeed() {
  const { user } = useAuthContext();
  const { profile } = useUserProfile();
  const {
    createPost,
    posts,
    loading: isLoading,
    error: postsError,
    likePost,
    savePost,
    deletePost,
    refetch,
    refreshPosts,
  } = useCommunityPosts();
  const { statistics, loading: statsLoading } = useCommunityStatistics();

  // Seed the active tab from ?tab=foryou|trending|latest so deep links
  // from /home (e.g. HomeTopEngaged "View all") land on the right tab.
  // Anything else falls back to "foryou".
  const searchParams = useSearchParams();
  const initialTab: FeedTab = (() => {
    const t = searchParams?.get('tab');
    return t === 'trending' || t === 'latest' || t === 'foryou' ? t : 'foryou';
  })();
  const [activeTab, setActiveTab] = useState<FeedTab>(initialTab);
  const [postContent, setPostContent] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<PostCategory>('general');
  const [filterCategory, setFilterCategory] = useState<PostCategory | null>(null);
  const [isPosting, setIsPosting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [postTags, setPostTags] = useState<string[]>([]);
  const [selectedTagFilter, setSelectedTagFilter] = useState<string | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<{
    type: 'video' | 'audio' | 'demo';
    file: File | null;
    url?: string;
    mediaFileId?: string;
  } | null>(null);
  const [selectedImages, setSelectedImages] = useState<{ url: string; storagePath: string; name: string }[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const MAX_IMAGES = 4;
  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB per image
  const [composerExpanded, setComposerExpanded] = useState(() => searchParams?.get('compose') === '1');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const [postCaret, setPostCaret] = useState<number | null>(null);

  // Click-outside, Escape and the mobile bottom-sheet branch all live in
  // EmojiPickerPopover now.
  useCaretRestore(postCaret, setPostCaret, textareaRef);

  const composerName = resolveDisplayName(profile as any, 'You');
  const composerAvatarSrc =
    (profile as any)?.avatar_url || undefined;

  // Track presence (preserved verbatim from prior implementation)
  useEffect(() => {
    if (!user) return;

    let isMounted = true;
    let presenceInterval: NodeJS.Timeout | null = null;
    let retryTimeout: NodeJS.Timeout | null = null;
    let consecutiveFailures = 0;
    const MAX_RETRIES = 3;
    const INITIAL_RETRY_DELAY = 1000;
    const MAX_RETRY_DELAY = 30000;
    const HEARTBEAT_INTERVAL = 2 * 60 * 1000;
    const REQUEST_TIMEOUT = 5000;

    const isOnline = () => {
      if (typeof navigator !== 'undefined') return navigator.onLine;
      return true;
    };

    const trackPresence = async (isRetry = false): Promise<void> => {
      if (!isOnline()) {
        if (!isRetry) console.debug('[Presence] Skipping - offline');
        return;
      }
      if (consecutiveFailures >= MAX_RETRIES && !isRetry) {
        console.debug('[Presence] Skipping - too many failures, will retry later');
        return;
      }
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
        const response = await fetch('/api/community/presence', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        if (consecutiveFailures > 0) console.debug('[Presence] Connection restored');
        consecutiveFailures = 0;
      } catch (err) {
        consecutiveFailures++;
        const isNetworkError =
          err instanceof TypeError &&
          (err.message.includes('Failed to fetch') ||
            err.message.includes('ERR_NAME_NOT_RESOLVED') ||
            err.message.includes('ERR_INTERNET_DISCONNECTED'));
        if (!isNetworkError || consecutiveFailures === 1) {
          console.debug('[Presence] Error:', err instanceof Error ? err.message : String(err));
        }
        if (
          consecutiveFailures < MAX_RETRIES &&
          isMounted &&
          !(err instanceof Error && err.name === 'AbortError')
        ) {
          const delay = Math.min(
            INITIAL_RETRY_DELAY * Math.pow(2, consecutiveFailures - 1),
            MAX_RETRY_DELAY
          );
          retryTimeout = setTimeout(() => {
            if (isMounted) trackPresence(true);
          }, delay);
        }
      }
    };

    const initialTimeout = setTimeout(() => {
      if (isMounted) trackPresence();
    }, 2000);

    const setupHeartbeat = () => {
      if (presenceInterval) clearInterval(presenceInterval);
      if (isOnline() && consecutiveFailures < MAX_RETRIES) {
        presenceInterval = setInterval(() => {
          if (isMounted && isOnline()) trackPresence();
        }, HEARTBEAT_INTERVAL);
      }
    };

    const handleOnline = () => {
      console.debug('[Presence] Network online, resuming tracking');
      consecutiveFailures = 0;
      trackPresence();
      setupHeartbeat();
    };
    const handleOffline = () => {
      console.debug('[Presence] Network offline, pausing tracking');
      if (presenceInterval) {
        clearInterval(presenceInterval);
        presenceInterval = null;
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
    }
    setupHeartbeat();

    return () => {
      isMounted = false;
      clearTimeout(initialTimeout);
      if (presenceInterval) clearInterval(presenceInterval);
      if (retryTimeout) clearTimeout(retryTimeout);
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      }
    };
  }, [user]);

  const categories: Array<{
    id: PostCategory;
    label: string;
    icon: any;
    color: string;
  }> = [
    { id: 'general', label: 'General', icon: MessageCircle, color: 'bg-accent' },
    { id: 'workshop', label: 'Workshops', icon: GraduationCap, color: 'bg-accent' },
    { id: 'audition', label: 'Auditions', icon: Mic, color: 'bg-accent' },
    { id: 'demo', label: 'Demos', icon: Play, color: 'bg-accent' },
    { id: 'tip', label: 'Tips', icon: Lightbulb, color: 'bg-accent' },
    { id: 'celebration', label: 'Celebrations', icon: PartyPopper, color: 'bg-accent' },
  ];

  const handleMediaSelect = async (
    type: 'video' | 'audio' | 'demo',
    file: File
  ) => {
    if (!user) {
      alert('You must be logged in to upload media');
      return;
    }

    const fileType = type === 'demo' ? 'audio' : type;
    const allowedVideoTypes = [
      'video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/mov',
    ];
    const allowedAudioTypes = [
      'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/x-m4a',
    ];

    const isVideo =
      file.type.startsWith('video/') || allowedVideoTypes.includes(file.type);
    const isAudio =
      file.type.startsWith('audio/') || allowedAudioTypes.includes(file.type);

    if (fileType === 'video' && !isVideo) {
      alert('Please select a valid video file (MP4, WebM, MOV, AVI)');
      return;
    }
    if (fileType === 'audio' && !isAudio) {
      alert('Please select a valid audio file (MP3, WAV, OGG, WebM, M4A)');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const supabase = createClient();
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(`File size exceeds ${maxSize / (1024 * 1024)}MB limit`);
        setIsUploading(false);
        return;
      }

      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${user.id}/posts/${type}/${Date.now()}-${sanitizedName}`;

      const uploadResult = await resumableUpload({
        bucket: 'user-media',
        path: fileName,
        file,
        supabase,
        chunkSize: 5 * 1024 * 1024,
        contentType: file.type,
        onProgress: (progress: UploadProgress) => {
          setUploadProgress(progress.percentage);
        },
      });

      if (!uploadResult.success || !uploadResult.publicUrl) {
        throw new Error(uploadResult.error || 'Upload failed');
      }

      const mediaFileData: any = {
        user_id: user.id,
        file_type: fileType,
        file_url: uploadResult.publicUrl,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type,
        visibility: 'public',
      };
      try {
        mediaFileData.status = 'active';
      } catch (e) {
        // status column may not exist, ignore
      }

      const { data: mediaFile, error: dbError } = await supabase
        .from('media_files')
        .insert(mediaFileData)
        .select('id')
        .single();

      if (dbError) {
        console.error('Error creating media file record:', dbError);
      }

      setSelectedMedia({
        type,
        file,
        url: uploadResult.publicUrl,
        mediaFileId: mediaFile?.id,
      });
    } catch (err: any) {
      console.error('Media upload error:', err);
      alert(err.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const sanitizeFileName = (name: string) =>
    name
      .normalize('NFKD')
      .replace(/[^\w.\-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^[._-]+|[._-]+$/g, '');

  const handleImagesSelect = async (files: FileList) => {
    if (!user) {
      alert('You must be logged in to upload images');
      return;
    }
    const remaining = MAX_IMAGES - selectedImages.length;
    if (remaining <= 0) {
      alert(`You can attach up to ${MAX_IMAGES} images per post.`);
      return;
    }
    const picked = Array.from(files).slice(0, remaining);
    const invalid = picked.find(
      (f) => !ALLOWED_IMAGE_TYPES.includes(f.type) || f.size > MAX_IMAGE_SIZE
    );
    if (invalid) {
      alert(
        `"${invalid.name}" is not a supported image (must be JPG, PNG, WEBP, or GIF under ${MAX_IMAGE_SIZE / (1024 * 1024)}MB).`
      );
      return;
    }
    setIsUploadingImages(true);
    try {
      const supabase = createClient();
      const uploaded: { url: string; storagePath: string; name: string }[] = [];
      for (const file of picked) {
        const safeName = sanitizeFileName(file.name) || 'image.bin';
        const uuid =
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        const path = `posts/${user.id}/images/${uuid}-${safeName}`;
        const { error: uploadError } = await supabase.storage
          .from('user-assets')
          .upload(path, file, {
            upsert: false,
            contentType: file.type || undefined,
            cacheControl: '3600',
          });
        if (uploadError) {
          alert(`Failed to upload ${file.name}: ${uploadError.message}`);
          continue;
        }
        const {
          data: { publicUrl },
        } = supabase.storage.from('user-assets').getPublicUrl(path);
        uploaded.push({ url: publicUrl, storagePath: path, name: file.name });
      }
      if (uploaded.length > 0) {
        setSelectedImages((prev) => [...prev, ...uploaded]);
      }
    } catch (err: any) {
      console.error('Image upload error:', err);
      alert(err?.message || 'Failed to upload images');
    } finally {
      setIsUploadingImages(false);
    }
  };

  const handleRemoveImage = async (index: number) => {
    const target = selectedImages[index];
    if (!target) return;
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    try {
      const supabase = createClient();
      await supabase.storage.from('user-assets').remove([target.storagePath]);
    } catch (err) {
      console.warn('Failed to clean orphan image storage object:', err);
    }
  };

  const handlePostSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!postContent.trim() || isPosting) return;

    setIsPosting(true);
    try {
      const media: { type: 'image' | 'video' | 'audio'; url?: string; urls?: string[] } | undefined = selectedImages.length > 0
        ? { type: 'image', urls: selectedImages.map((img) => img.url) }
        : selectedMedia
        ? {
            type:
              selectedMedia.type === 'demo'
                ? ('audio' as const)
                : (selectedMedia.type as 'video' | 'audio'),
            url: selectedMedia.url || '',
          }
        : undefined;

      const newPost = await createPost(
        postContent.trim(),
        media,
        postTags.length > 0 ? postTags : undefined,
        selectedCategory
      );

      if (selectedMedia?.mediaFileId && newPost?.id) {
        const supabase = createClient();
        const updateData: any = { post_id: newPost.id };
        try {
          updateData.status = 'published';
        } catch (e) {
          // ignore
        }
        const { error: linkError } = await supabase
          .from('media_files')
          .update(updateData)
          .eq('id', selectedMedia.mediaFileId);
        if (linkError) console.error('Error linking media to post:', linkError);
      }

      setPostContent('');
      setPostTags([]);
      setSelectedCategory('general');
      setSelectedMedia(null);
      setSelectedImages([]);
      setComposerExpanded(false);
    } catch (error) {
      console.error('Error creating post:', error);
      alert(
        `Failed to create post: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setIsPosting(false);
    }
  };

  const getCategoryIcon = (cat: string) => {
    const c = categories.find((x) => x.id === cat);
    return c ? c.icon : MessageCircle;
  };
  const getCategoryColor = (cat: string) => {
    const c = categories.find((x) => x.id === cat);
    return c ? c.color : 'bg-accent';
  };

  const handleLike = async (postId: string) => {
    try {
      await likePost(postId);
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleSave = async (postId: string) => {
    try {
      await savePost(postId);
    } catch (error) {
      console.error('Error saving post:', error);
    }
  };

  const handleDelete = async (postId: string) => {
    if (
      confirm('Are you sure you want to delete this post? This action cannot be undone.')
    ) {
      try {
        await deletePost(postId);
        await refetch();
      } catch (error) {
        console.error('Error deleting post:', error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to delete post. Please try again.';
        alert(errorMessage);
      }
    }
  };

  const handleCopyLink = async (postId: string) => {
    try {
      const baseUrl = window.location.origin;
      const postUrl = `${baseUrl}/community/posts/${postId}`;
      await navigator.clipboard.writeText(postUrl);
    } catch (error) {
      console.error('Error copying link:', error);
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    const { next, caret } = insertEmojiAtCursor(textareaRef.current, postContent, emoji);
    setPostContent(next);
    setPostCaret(caret);
  };

  const handleTagClick = (t: string) => {
    setSelectedTagFilter(selectedTagFilter === t ? null : t);
  };

  const getFilteredAndSortedPosts = () => {
    let filtered = [...posts];
    if (filterCategory && filterCategory !== 'general') {
      filtered = filtered.filter((post) => post.category === filterCategory);
    }
    if (selectedTagFilter) {
      filtered = filtered.filter(
        (post) => post.tags && post.tags.includes(selectedTagFilter)
      );
    }
    switch (activeTab) {
      case 'trending':
        filtered.sort((a, b) => {
          const engagementA = (a.likes || 0) + (a.comments || 0) + (a.shares || 0);
          const engagementB = (b.likes || 0) + (b.comments || 0) + (b.shares || 0);
          return engagementB - engagementA;
        });
        break;
      case 'latest':
      case 'foryou':
      default:
        filtered.sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA;
        });
        break;
    }
    return filtered;
  };

  const filteredPosts = getFilteredAndSortedPosts();

  const tabs: Array<{ id: FeedTab; label: string; icon: any }> = [
    { id: 'foryou', label: 'For You', icon: Sparkles },
    { id: 'trending', label: 'Trending', icon: TrendingUp },
    { id: 'latest', label: 'Latest', icon: Clock },
  ];

  return (
    <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-4">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
        {/* Main feed */}
        <div className="w-full max-w-[720px] lg:mx-0 mx-auto space-y-4">
          {/* Composer anchor */}
          <div id="composer" />

          {/* Composer */}
          <Card>
            <form onSubmit={handlePostSubmit} className="space-y-3">
              {!composerExpanded ? (
                <button
                  type="button"
                  onClick={() => setComposerExpanded(true)}
                  className="flex items-center gap-3 w-full"
                >
                  <Avatar
                    src={composerAvatarSrc}
                    name={composerName}
                    size={32}
                  />
                  <span className="flex-1 text-left text-text-secondary text-sm">
                    Share something with the community...
                  </span>
                  <Video className="w-4 h-4 text-text-secondary" strokeWidth={1.75} />
                  <Music className="w-4 h-4 text-text-secondary" strokeWidth={1.75} />
                </button>
              ) : (
                <>
                  <div className="flex items-start gap-3">
                    <Avatar
                      src={composerAvatarSrc}
                      name={composerName}
                      size={40}
                    />
                    <div className="flex-1">
                      <Textarea
                        ref={textareaRef}
                        autoFocus
                        value={postContent}
                        onChange={(e) => setPostContent(e.target.value)}
                        placeholder="What's on your mind?"
                        className="min-h-[100px] bg-surface-3 border-border-subtle text-text-primary placeholder:text-text-secondary"
                      />
                    </div>
                  </div>

                  <TagAutocomplete
                    selectedTags={postTags}
                    onTagsChange={setPostTags}
                    maxTags={10}
                    placeholder="Add tags to help others find your post..."
                  />

                  {isUploading && (
                    <div className="p-3 bg-surface-3 rounded-sm border border-border-subtle">
                      <div className="flex items-center gap-2 text-xs text-text-secondary mb-2">
                        <span>Uploading... {uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-surface-2 rounded-full h-2">
                        <div
                          className="bg-accent h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {selectedMedia && selectedMedia.url && !isUploading && (
                    <div className="p-3 bg-surface-3 rounded-sm border border-border-subtle flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {selectedMedia.type === 'video' && (
                          <Video className="w-4 h-4 text-text-secondary" strokeWidth={1.75} />
                        )}
                        {selectedMedia.type === 'audio' && (
                          <Music className="w-4 h-4 text-text-secondary" strokeWidth={1.75} />
                        )}
                        {selectedMedia.type === 'demo' && (
                          <Headphones className="w-4 h-4 text-text-secondary" strokeWidth={1.75} />
                        )}
                        <span className="text-xs text-text-primary">
                          {selectedMedia.file?.name || 'Media file'}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedMedia(null)}
                        className="text-status-busy hover:opacity-80"
                        aria-label="Remove media"
                      >
                        <X className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                    </div>
                  )}

                  {(selectedImages.length > 0 || isUploadingImages) && (
                    <div className="p-3 bg-surface-3 rounded-sm border border-border-subtle">
                      {isUploadingImages && (
                        <p className="text-xs text-text-secondary mb-2">Uploading images…</p>
                      )}
                      {selectedImages.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {selectedImages.map((img, i) => (
                            <div
                              key={img.storagePath}
                              className="relative w-20 h-20 rounded-sm overflow-hidden border border-border-subtle bg-surface-2"
                            >
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img
                                src={img.url}
                                alt={img.name}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveImage(i)}
                                aria-label={`Remove ${img.name}`}
                                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black"
                              >
                                <X className="w-3 h-3" strokeWidth={2} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-[11px] text-text-tertiary mt-2">
                        {selectedImages.length}/{MAX_IMAGES} images
                      </p>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {categories.map((c) => {
                      const Icon = c.icon;
                      const active = selectedCategory === c.id;
                      return (
                        <button
                          type="button"
                          key={c.id}
                          onClick={() => setSelectedCategory(c.id)}
                          className={cn(
                            'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-sm text-xs font-medium transition-colors',
                            active
                              ? 'bg-accent text-text-on-accent'
                              : 'bg-surface-3 text-text-secondary border border-border-subtle hover:bg-surface-2'
                          )}
                        >
                          <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                          {c.label}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex justify-between items-center flex-wrap gap-2">
                    <div className="flex gap-1">
                      <div className="relative">
                        <button
                          ref={emojiButtonRef}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setShowEmojiPicker((v) => !v)}
                          aria-expanded={showEmojiPicker}
                          aria-haspopup="dialog"
                          className="inline-flex items-center gap-1.5 h-7 px-2.5 text-xs text-text-secondary hover:text-text-primary hover:bg-surface-2 rounded-sm transition-colors"
                        >
                          <Smile className="h-3.5 w-3.5" strokeWidth={1.75} />
                          Emoji
                        </button>
                        {showEmojiPicker && (
                          <EmojiPickerPopover
                            anchorRef={emojiButtonRef}
                            onSelect={handleEmojiSelect}
                            onClose={() => setShowEmojiPicker(false)}
                          />
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                        multiple
                        onChange={(e) => {
                          const files = e.target.files;
                          if (files && files.length > 0) {
                            handleImagesSelect(files);
                            e.target.value = '';
                          }
                        }}
                        className="hidden"
                        id="image-upload"
                      />
                      <button
                        type="button"
                        onClick={() => document.getElementById('image-upload')?.click()}
                        disabled={selectedImages.length >= MAX_IMAGES || isUploadingImages || !!selectedMedia}
                        title={selectedMedia ? 'Remove the attached video/audio first' : selectedImages.length >= MAX_IMAGES ? `Up to ${MAX_IMAGES} images per post` : 'Add images'}
                        className="inline-flex items-center gap-1.5 h-7 px-2.5 text-xs text-text-secondary hover:text-text-primary hover:bg-surface-2 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ImageIcon className="h-3.5 w-3.5" strokeWidth={1.75} />
                        Image
                      </button>
                      <input
                        type="file"
                        accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/mov"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleMediaSelect('video', file);
                            e.target.value = '';
                          }
                        }}
                        className="hidden"
                        id="video-upload"
                      />
                      <button
                        type="button"
                        onClick={() => document.getElementById('video-upload')?.click()}
                        disabled={selectedImages.length > 0}
                        title={selectedImages.length > 0 ? 'Remove attached images first' : 'Add a video'}
                        className="inline-flex items-center gap-1.5 h-7 px-2.5 text-xs text-text-secondary hover:text-text-primary hover:bg-surface-2 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Video className="h-3.5 w-3.5" strokeWidth={1.75} />
                        Video
                      </button>
                      <input
                        type="file"
                        accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/webm,audio/x-m4a"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleMediaSelect('audio', file);
                            e.target.value = '';
                          }
                        }}
                        className="hidden"
                        id="audio-upload"
                      />
                      <button
                        type="button"
                        onClick={() => document.getElementById('audio-upload')?.click()}
                        disabled={selectedImages.length > 0}
                        title={selectedImages.length > 0 ? 'Remove attached images first' : 'Add audio'}
                        className="inline-flex items-center gap-1.5 h-7 px-2.5 text-xs text-text-secondary hover:text-text-primary hover:bg-surface-2 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Music className="h-3.5 w-3.5" strokeWidth={1.75} />
                        Audio
                      </button>
                      <input
                        type="file"
                        accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/webm,audio/x-m4a"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleMediaSelect('demo', file);
                            e.target.value = '';
                          }
                        }}
                        className="hidden"
                        id="demo-upload"
                      />
                      <button
                        type="button"
                        onClick={() => document.getElementById('demo-upload')?.click()}
                        disabled={selectedImages.length > 0}
                        title={selectedImages.length > 0 ? 'Remove attached images first' : 'Add a demo'}
                        className="inline-flex items-center gap-1.5 h-7 px-2.5 text-xs text-text-secondary hover:text-text-primary hover:bg-surface-2 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Headphones className="h-3.5 w-3.5" strokeWidth={1.75} />
                        Demo
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setComposerExpanded(false);
                          setShowEmojiPicker(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        disabled={!postContent.trim() || isPosting || isUploadingImages || isUploading}
                      >
                        {isPosting ? 'Posting...' : 'Post'}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </form>
          </Card>

          {/* Mobile horizontal category chip strip — denser than the
              vertical sidebar, scrolls horizontally if it overflows. */}
          <div className="lg:hidden -mx-4 px-4 overflow-x-auto">
            <div className="flex items-center gap-1.5 pb-1 min-w-max">
              <button
                type="button"
                onClick={() => setFilterCategory(null)}
                className={cn(
                  'inline-flex h-7 items-center px-2.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors',
                  !filterCategory
                    ? 'bg-accent text-text-on-accent'
                    : 'bg-surface-2 text-text-secondary border border-border-subtle hover:bg-surface-3'
                )}
              >
                All
              </button>
              {categories.map((c) => {
                const active = filterCategory === c.id;
                const Icon = c.icon;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setFilterCategory(active ? null : c.id)}
                    className={cn(
                      'inline-flex h-7 items-center gap-1.5 px-2.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors',
                      active
                        ? 'bg-accent text-text-on-accent'
                        : 'bg-surface-2 text-text-secondary border border-border-subtle hover:bg-surface-3'
                    )}
                  >
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tabs */}
          <div className="sticky top-[48px] z-10 bg-surface-0 -mx-4 px-4 py-2">
            <div className="flex border-b border-border-subtle">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      setActiveTab(tab.id);
                      setFilterCategory(null);
                    }}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors relative -mb-[1px]',
                      active
                        ? 'text-accent border-b-2 border-accent'
                        : 'text-text-secondary hover:text-text-primary border-b-2 border-transparent'
                    )}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {(selectedTagFilter || filterCategory) && (
            <div className="flex flex-wrap items-center gap-2 p-3 bg-accent-soft border border-border-subtle rounded-sm">
              {filterCategory && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-text-primary">
                    Category:{' '}
                    <strong>
                      {categories.find((c) => c.id === filterCategory)?.label ||
                        filterCategory}
                    </strong>
                  </span>
                  <button
                    type="button"
                    onClick={() => setFilterCategory(null)}
                    className="h-6 w-6 flex items-center justify-center rounded-sm text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors"
                    aria-label="Clear category filter"
                  >
                    <X className="h-3 w-3" strokeWidth={1.75} />
                  </button>
                </div>
              )}
              {selectedTagFilter && (
                <div className="flex items-center gap-2">
                  <TagIcon className="h-4 w-4 text-accent" strokeWidth={1.75} />
                  <span className="text-sm text-text-primary">
                    Tag: <strong>#{selectedTagFilter}</strong>
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedTagFilter(null)}
                    className="h-6 w-6 flex items-center justify-center rounded-sm text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors"
                    aria-label="Clear tag filter"
                  >
                    <X className="h-3 w-3" strokeWidth={1.75} />
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto" />
                <p className="text-text-secondary text-sm mt-2">Loading posts...</p>
              </div>
            ) : postsError ? (
              <Card>
                <p className="text-status-busy font-semibold mb-2">Error loading posts</p>
                <p className="text-text-secondary text-xs mb-4">{postsError}</p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => refreshPosts?.() || refetch()}
                >
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.75} />
                  Try Again
                </Button>
              </Card>
            ) : filteredPosts.length === 0 ? (
              filterCategory || selectedTagFilter ? (
                <EmptyState
                  size="lg"
                  icon={MessageCircle}
                  title="No posts match these filters"
                  description="Try clearing the category or tag filter to see everything that's been posted recently."
                  action={
                    <Button
                      variant="secondary"
                      size="md"
                      onClick={() => {
                        setFilterCategory(null);
                        setSelectedTagFilter(null);
                      }}
                    >
                      Clear filters
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  size="lg"
                  icon={MessageCircle}
                  title="No posts yet"
                  description="When community members start sharing, their posts will show up here. Be the first to post something and start the conversation."
                  action={
                    <Button
                      variant="primary"
                      size="md"
                      onClick={() => setComposerExpanded(true)}
                    >
                      Create your first post
                    </Button>
                  }
                />
              )
            ) : (
              filteredPosts.map((post) => (
                <PostItem
                  key={post.id}
                  post={post}
                  currentUserId={user?.id}
                  onLike={handleLike}
                  onSave={handleSave}
                  onDelete={handleDelete}
                  onCopyLink={handleCopyLink}
                  getCategoryIcon={getCategoryIcon}
                  getCategoryColor={getCategoryColor}
                  onPostUpdated={refetch}
                />
              ))
            )}
          </div>
        </div>

        {/* Sidebar — hidden on mobile (mobile already has the horizontal
            category chip strip + composer up top; the stats / trending /
            jobs widgets are desktop-only to keep the mobile feed lean). */}
        <aside className="hidden lg:block space-y-4">
          <div className="hidden lg:block">
            <PostCategoryNavigation
              activeCategory={filterCategory || undefined}
              onCategoryChange={(category) => {
                setFilterCategory(
                  filterCategory === category ? null : (category as PostCategory)
                );
              }}
            />
          </div>

          <RecentJobsWidget />

          <Card>
            <div className="flex items-center gap-2 mb-3">
              <Activity className="h-4 w-4 text-accent" strokeWidth={1.75} />
              <p className="text-text-primary font-semibold text-sm">Activity Today</p>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-text-secondary text-xs">Trending</span>
                <span className="text-text-primary text-xs font-semibold">
                  {statsLoading ? '—' : statistics?.trending.topics || 0} topics
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-text-secondary text-xs">Active</span>
                <span className="text-text-primary text-xs font-semibold">
                  {statsLoading ? '—' : statistics?.active.formatted || '0'} users
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-text-secondary text-xs">Recent</span>
                <span className="text-text-primary text-xs font-semibold">
                  {statsLoading ? '—' : statistics?.recent.posts || 0} posts
                </span>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4 text-accent" strokeWidth={1.75} />
              <p className="text-text-primary font-semibold text-sm">Trending Topics</p>
            </div>
            {statsLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-accent mx-auto" />
              </div>
            ) : statistics?.trending.topTags.length ? (
              <div className="space-y-1">
                {statistics.trending.topTags.map((topic) => (
                  <button
                    type="button"
                    key={topic.tag}
                    onClick={() => handleTagClick(topic.tag)}
                    className={cn(
                      'flex justify-between items-center w-full p-2 rounded-sm transition-colors text-left',
                      selectedTagFilter === topic.tag
                        ? 'bg-accent-soft'
                        : 'hover:bg-surface-3'
                    )}
                  >
                    <span className="text-text-primary text-xs">#{topic.tag}</span>
                    <span className="text-text-secondary text-[11px]">
                      {topic.count} posts
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-text-tertiary text-xs">No trending topics yet</p>
            )}
          </Card>
        </aside>
      </div>
    </div>
  );
}
